import { useState } from 'react';
import { ChevronDown, ChevronUp, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FilterOptions } from '@/types';

interface AdvancedFilterControlsProps {
  category?: string;
  assignedDoctor?: string;
  priority?: string;
  isFavorite?: boolean;
  tags?: string[];
  filterOptions?: FilterOptions;
  onCategoryChange: (value?: string) => void;
  onAssignedDoctorChange: (value?: string) => void;
  onPriorityChange: (value?: string) => void;
  onFavoriteChange: (value?: boolean) => void;
  onTagsChange: (value?: string[]) => void;
}

export function AdvancedFilterControls({
  category,
  assignedDoctor,
  priority,
  isFavorite,
  tags,
  filterOptions,
  onCategoryChange,
  onAssignedDoctorChange,
  onPriorityChange,
  onFavoriteChange,
  onTagsChange,
}: AdvancedFilterControlsProps) {
  const [expanded, setExpanded] = useState(false);

  const hasAdvancedFilters = !!(category || assignedDoctor || priority || isFavorite || tags?.length);

  const handleTagClick = (tag: string) => {
    const current = tags || [];
    if (current.includes(tag)) {
      const next = current.filter((t) => t !== tag);
      onTagsChange(next.length ? next : undefined);
    } else {
      onTagsChange([...current, tag]);
    }
  };

  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        className="h-8 text-xs"
      >
        Advanced Filters
        {hasAdvancedFilters && (
          <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
            active
          </Badge>
        )}
        {expanded ? (
          <ChevronUp className="ml-1 h-3 w-3" />
        ) : (
          <ChevronDown className="ml-1 h-3 w-3" />
        )}
      </Button>

      {expanded && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-center">
          {/* Category */}
          <Select
            value={category || ''}
            onValueChange={(v) => onCategoryChange(v || undefined)}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Categories</SelectItem>
              {filterOptions?.categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Doctor */}
          <Select
            value={assignedDoctor || ''}
            onValueChange={(v) => onAssignedDoctorChange(v || undefined)}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="Doctor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Doctors</SelectItem>
              {filterOptions?.doctors.map((doc) => (
                <SelectItem key={doc.id} value={doc.email}>
                  {doc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Priority */}
          <Select
            value={priority || ''}
            onValueChange={(v) => onPriorityChange(v || undefined)}
          >
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Priorities</SelectItem>
              {filterOptions?.priorities.map((p) => (
                <SelectItem key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Favorites toggle */}
          <Button
            variant={isFavorite ? 'default' : 'outline'}
            size="sm"
            onClick={() => onFavoriteChange(isFavorite ? undefined : true)}
          >
            <Star className={`mr-1 h-3 w-3 ${isFavorite ? 'fill-current' : ''}`} />
            Favorites
          </Button>

          {/* Tags */}
          {filterOptions?.tags && filterOptions.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {filterOptions.tags.slice(0, 15).map((tag) => (
                <Badge
                  key={tag}
                  variant={tags?.includes(tag) ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => handleTagClick(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
